import { useState, useEffect } from "react";
import { Apis } from "../../../lib/Apis";

const getApiError = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

/** Unwrap `{ success, statusCode, data }` response model. */
const unwrapResponseBody = (body) => {
  if (body == null) return null;
  if (body?.success === false) {
    throw new Error(body?.message || "Request failed.");
  }
  if (
    body &&
    typeof body === "object" &&
    "data" in body &&
    ("success" in body || "statusCode" in body)
  ) {
    return body.data;
  }
  return body;
};

const unwrapListPayload = (body) => {
  const payload = unwrapResponseBody(body) ?? body ?? {};
  const items = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.corporateArchitectureDesigns)
      ? payload.corporateArchitectureDesigns
      : Array.isArray(payload)
        ? payload
        : [];
  const pagination = payload?.pagination ?? body?.pagination ?? null;
  const totalCount =
    payload?.totalCount ??
    pagination?.totalRecords ??
    pagination?.totalCount ??
    pagination?.totalItems ??
    items.length;
  return { items, pagination, totalCount, payload };
};

// --- Corporate Architecture Design ---
export const useCorporateArchitectureDesign = () => {
  const getCorporateArchitectureDesign = async (
    setCorporateArchitectureDesignData,
    setTotal,
    offset = 0,
    limit = 10,
    searchFilter = "",
  ) => {
    try {
      const params = {};

      if (searchFilter && searchFilter.trim()) {
        params.searchFilter = searchFilter.trim();
      }

      const response = await Apis.getCorporateArchitectureDesign(offset, limit, params);
      if (response?.status === 200) {
        const { items, pagination, totalCount, payload } = unwrapListPayload(
          response?.data,
        );
        setCorporateArchitectureDesignData(items);
        setTotal(totalCount);
        return { data: items, pagination, totalCount, ...payload };
      }
      throw new Error(
        response?.data?.message || "Failed to fetch corporate architecture designs.",
      );
    } catch (error) {
      console.error("Error fetching corporate architecture design:", error);
      throw error;
    }
  };

  const searchCorporateArchitectureDesign = async (searchFilter, offset = 0, limit = 10) => {
    try {
      const params = {};

      if (searchFilter && searchFilter.trim()) {
        params.searchFilter = searchFilter.trim();
      }

      const response = await Apis.getCorporateArchitectureDesign(offset, limit, params);
      const { items, pagination, totalCount } = unwrapListPayload(
        response?.data,
      );
      return { data: items, pagination, totalCount };
    } catch (error) {
      console.error(
        "🚀 ~ file: useHook.js ~ line 64 ~ searchCorporateArchitectureDesign ~ error",
        error,
      );
      throw error;
    }
  };

  const addCorporateArchitectureDesign = async (data) => {
    try {
      const response = await Apis.addCorporateArchitectureDesign(data);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error adding corporate architecture design:", error);
      throw new Error(getApiError(error, "Failed to add corporate architecture design."));
    }
  };

  const updateCorporateArchitectureDesign = async (id, data) => {
    try {
      const response = await Apis.updateCorporateArchitectureDesign(id, data);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error updating corporate architecture design:", error);
      throw new Error(getApiError(error, "Failed to update corporate architecture design."));
    }
  };

  const deleteCorporateArchitectureDesign = async (id) => {
    try {
      const response = await Apis.deleteCorporateArchitectureDesign(id);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error deleting corporate architecture design:", error);
      throw new Error(getApiError(error, "Failed to delete corporate architecture design."));
    }
  };

  const getCorporateArchitectureDesignById = async (id, setHouseData) => {
    try {
      const response = await Apis.getCorporateArchitectureDesignById(id);
      const payload = unwrapResponseBody(response?.data);
      if (typeof setHouseData === "function") setHouseData(payload);
      return payload;
    } catch (error) {
      console.error("Error fetching corporate architecture design by id:", error);
      throw error;
    }
  };

  const showCorporateArchitectureDesignOnHomeScreen = async (corporateArchitectureDesignId, homeScreen) => {
    try {
      const data = {
        corporateArchitectureDesignId,
        homeScreen: homeScreen ? 1 : 0,
      };
      const response = await Apis.addCorporateArchitectureDesignToHomeScreen(data);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to update home screen status.");
      }
      return unwrapResponseBody(body) ?? body;
    } catch (error) {
      console.error("Error updating home screen status:", error);
      throw error;
    }
  };

  const updateCorporateArchitectureDesignPosition = async (corporateArchitectureDesignId, newPosition) => {
    try {
      const response = await Apis.updateCorporateArchitectureDesignPosition({
        corporateArchitectureDesignId,
        newPosition,
      });
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error updating corporate architecture design position:", error);
      throw error;
    }
  };

  const updateCorporateArchitectureDesignPrimaryImage = async (id) => {
    try {
      const response = await Apis.updateCorporateArchitectureDesignPrimaryImage(id);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error updating corporate architecture design primary image:", error);
      throw error;
    }
  };

  return {
    getCorporateArchitectureDesign,
    addCorporateArchitectureDesign,
    updateCorporateArchitectureDesign,
    deleteCorporateArchitectureDesign,
    getCorporateArchitectureDesignById,
    showCorporateArchitectureDesignOnHomeScreen,
    searchCorporateArchitectureDesign,
    updateCorporateArchitectureDesignPosition,
    updateCorporateArchitectureDesignPrimaryImage,
  };
};
